import { useEffect, useState } from 'react'
import { Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { FadeModal } from '@/components/ui/fade-modal'
import { splitAddressDisplay } from '@/lib/address'
import { searchPropertiesByPrefix, type PropertySuggestion } from '@/lib/callables'
import { callableErrorMessage } from '@/lib/callable-errors'
import { addPropertyToCollection } from '@/lib/collections'

export function AddPropertyToCollectionModal({
  open,
  onClose,
  uid,
  collectionId,
  existingPropertyIds,
  onAdded,
}: {
  open: boolean
  onClose: () => void
  uid: string
  collectionId: string
  existingPropertyIds: Set<string>
  onAdded: (item: {
    propertyId: string
    fullAddress: string
  }) => void
}) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<PropertySuggestion[]>([])
  const [loading, setLoading] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setQuery('')
      setResults([])
      setLoading(false)
      setBusyId(null)
      setError(null)
      setNotice(null)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const q = query.trim()
    if (q.length < 3) {
      setResults([])
      setLoading(false)
      return
    }
    let cancelled = false
    const timer = window.setTimeout(async () => {
      setLoading(true)
      setError(null)
      try {
        const data = await searchPropertiesByPrefix(q)
        if (!cancelled) setResults(data.properties ?? [])
      } catch (e) {
        if (!cancelled) {
          setResults([])
          setError(callableErrorMessage(e))
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, 250)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [query, open])

  async function onSelect(item: PropertySuggestion) {
    if (busyId) return
    if (existingPropertyIds.has(item.id)) {
      setNotice('Already in this collection')
      return
    }
    setBusyId(item.id)
    setError(null)
    setNotice(null)
    try {
      await addPropertyToCollection(uid, collectionId, item.id, item.fullAddress)
      onAdded({ propertyId: item.id, fullAddress: item.fullAddress })
      setNotice('Added to collection')
      window.setTimeout(onClose, 500)
    } catch (e) {
      setError(callableErrorMessage(e))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <FadeModal
      open={open}
      onClose={onClose}
      labelledBy="add-property-title"
      className="z-[80]"
      panelClassName="overflow-visible"
    >
      <div className="overflow-visible rounded-3xl bg-white p-6 shadow-2xl">
        <h2 id="add-property-title" className="text-lg font-bold text-navy">
          Add property
        </h2>
        <p className="mt-1 text-sm text-muted">Search by address and tap a result to add it.</p>

        <div className="relative z-10 mt-4">
          <Search className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted" />
          <Input
            autoFocus
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setNotice(null)
            }}
            placeholder="Search any Australian address…"
            className="h-12 pl-10 pr-10 text-base"
            aria-label="Search properties by address"
          />
          {loading ? (
            <Spinner className="absolute right-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2" />
          ) : null}

          {results.length > 0 ? (
            <ul className="absolute left-0 right-0 top-full z-30 mt-1 max-h-72 overflow-y-auto rounded-2xl border border-border bg-white py-1 shadow-xl">
              {results.map((item) => {
                const { street, locality } = splitAddressDisplay(item.fullAddress)
                const alreadyIn = existingPropertyIds.has(item.id)
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      disabled={Boolean(busyId)}
                      onClick={() => void onSelect(item)}
                      className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition hover:bg-surface disabled:opacity-60"
                    >
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-navy">{street}</span>
                        {locality ? (
                          <span className="block text-xs text-muted">{locality}</span>
                        ) : null}
                      </span>
                      {busyId === item.id ? (
                        <Spinner className="h-4 w-4 shrink-0" />
                      ) : alreadyIn ? (
                        <span className="shrink-0 text-xs text-muted">Added</span>
                      ) : null}
                    </button>
                  </li>
                )
              })}
            </ul>
          ) : query.trim().length >= 3 && !loading ? (
            <p className="absolute left-0 right-0 top-full z-30 mt-1 rounded-2xl border border-border bg-white px-4 py-3 text-sm text-muted shadow-xl">
              No matching properties.
            </p>
          ) : null}
        </div>

        {error ? <p className="mt-2 text-sm text-danger">{error}</p> : null}
        {notice ? <p className="mt-2 text-sm text-success">{notice}</p> : null}

        <div className="mt-4 flex justify-end">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </FadeModal>
  )
}
